import NextLink from 'next/link';
import styles from './TopBanner.module.scss';
import Typography from '../Typography/Typography';
import clsx from 'clsx';
import { Link, UspItem } from '~/lib/data-contract';
import { Image } from '~/shared/components';
import 'swiper/css/autoplay';
import { useEffect, useRef } from 'react';

interface TopBannerProps {
    top?: {
        color: string;
        background: string;
        text: string;
    };
    campaign?: {
        color: string;
        background: string;
        text: string;
        link?: Link;
    };
    uspLinks?: {
        color: string;
        background: string;
        usp: UspItem[];
    };
}

const TopBanner = ({ top, campaign, uspLinks }: TopBannerProps) => {
    const ref = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (ref.current && campaign && uspLinks) {
            const parent = ref.current;
            const active = styles.active;
            const next = styles.next;

            const startSlide = parent.children[1] as HTMLDivElement;

            ref.current.style.height = `${startSlide.offsetHeight}px`;

            const interval = setInterval(() => {
                parent?.querySelector(`.${active}`)?.classList.add(next);

                const elem = parent?.querySelector(`.${next}:not(.${active})`) as HTMLDivElement;
                elem.classList.replace(next, active);

                parent.style.height = `${elem?.offsetHeight}px`;

                setTimeout(() => {
                    parent?.querySelector(`.${active}.${next}`)?.classList.remove(active);
                }, 300);
            }, 8000);

            return () => clearInterval(interval);
        }
    }, []);

    return (
        <div className={styles.component}>
            {top && (
                <div
                    className={styles.banner}
                    style={{ backgroundColor: top.background, color: top.color }}
                >
                    <div className={styles.campaign}>
                        <Typography as="p" variant="paragraph-S">
                            {top.text}
                        </Typography>
                    </div>
                </div>
            )}

            <div
                className={clsx({
                    [styles.slider]: campaign && uspLinks,
                })}
                ref={ref}
            >
                {uspLinks && (
                    <div
                        className={clsx(styles.banner, styles.next)}
                        style={{ backgroundColor: uspLinks.background, color: uspLinks.color }}
                    >
                        <div className={styles.usplinks}>
                            {uspLinks.usp.map((item, index) => {
                                const component = (
                                    <div className={styles.usp} key={index}>
                                        <Image
                                            src={item.image.src ?? ''}
                                            width={18}
                                            height={18}
                                            layout="intrinsic"
                                            alt={item.image?.alt ?? ''}
                                            disableSkeleton
                                        />
                                        <Typography variant="paragraph-S">
                                            {item.headline}
                                        </Typography>
                                    </div>
                                );

                                return item.link ? (
                                    <NextLink
                                        href={item.link?.url ?? ''}
                                        target={item.link.target}
                                        title={item.link.title}
                                        className={styles.usplink}
                                    >
                                        {component}
                                    </NextLink>
                                ) : (
                                    component
                                );
                            })}
                        </div>
                    </div>
                )}

                {campaign && (
                    <div
                        className={clsx(styles.banner, styles.active)}
                        style={{ backgroundColor: campaign.background, color: campaign.color }}
                    >
                        <div className={styles.dot}></div>
                        <div className={clsx(styles.campaign, styles.withdot)}>
                            <Typography
                                as="div"
                                variant="paragraph-S"
                                dangerouslySetInnerHTML={{ __html: campaign.text }}
                            />

                            {campaign.link && (
                                <Typography variant="paragraph-S">
                                    <NextLink
                                        href={campaign.link.url ?? ''}
                                        target={campaign.link.target}
                                        title={campaign.link.title}
                                        className={styles.link}
                                    >
                                        {campaign.link.title}
                                    </NextLink>
                                </Typography>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default TopBanner;
